import { Chain, LogLevel, RenNetwork, SimpleLogger, Tokens, } from "@renproject/interfaces";
import { MultiProvider } from "@renproject/provider";
import { RenVMProvider, unmarshalFees } from "@renproject/rpc";
import { getGatewayAddress, getTokenAddress, NetworkDetails, resolveSendCall, stringToNetwork, utils, } from "@renproject/utils";
import { BurnAndRelease } from "./burnAndRelease";
import { LockAndMint } from "./lockAndMint";
/**
 * This is the main exported class.
 *
 * ```typescript
 * import RenJS from "@renproject/ren";
 * ```
 *
 * It's initialized with a network, which controls both the RenVM network and
 * Ethereum chain to use:
 *
 * ```typescript
 * new RenJS(); // Same as `new RenJS("mainnet");`
 * new RenJS("testnet");
 * new RenJS({ ...NetworkMainnet, lightnodeURL: "custom lightnode URL" });
 * ```
 *
 * A second optional parameter lets you provide a RenVM RPC provider or a
 * lightnode URL.
 *
 * It then exposes two main functions:
 * 1. [[lockAndMint]] - for transferring assets to Ethereum.
 * 2. [[burnAndRelease]] - for transferring assets out of Ethereum.
 */
export default class RenJS {
    /**
     * Takes a Network object that contains relevant addresses.
     * @param network One of "mainnet" (or empty), "testnet" or a custom
     *                Network object.
     */
    constructor(network, providerOrConfig) {
        // Not static
        this.utils = utils;
        /**
         * Submits the commitment and transaction to RenVM, and then submits the
         * signature to the adapter address.
         *
         * @param params See [[LockAndMintParams]].
         * @returns An instance of [[LockAndMint]].
         */
        this.lockAndMint = (params) => {
            if (params.sendTo && !params.contractFn) {
                params = resolveSendCall(this.network, params);
            }
            else if (params.sendTo) {
                const { sendTo, contractFn, contractParams, txConfig, ...restOfParams } = params;
                params = { ...restOfParams, contractCalls: [{ sendTo, contractFn, contractParams, txConfig }] };
            }
            return new LockAndMint(this.renVM, this.network, params, this.logger);
        };
        /**
         * Submits a burn log to RenVM.
         *
         * @param params See [[BurnAndReleaseParams]].
         * @returns An instance of [[BurnAndRelease]].
         */
        this.burnAndRelease = (params) => {
            if (params.sendTo && !params.contractFn) {
                params = resolveSendCall(this.network, params);
            }
            else if (params.sendTo) {
                const { sendTo, contractFn, contractParams, txConfig, ...restOfParams } = params;
                params = { ...restOfParams, contractCalls: [{ sendTo, contractFn, contractParams, txConfig }] };
            }
            return new BurnAndRelease(this.renVM, this.network, params, this.logger);
        };
        this.getTokenAddress = (web3, token) => getTokenAddress(this.network, web3, token);
        this.getGatewayAddress = (web3, token) => getGatewayAddress(this.network, web3, token);
        this.getFees = () => this.renVM.queryFees().then(unmarshalFees);
        this.network = stringToNetwork(network);
        let provider;
        let config;
        if (providerOrConfig && (typeof providerOrConfig === "string" || providerOrConfig.sendMessage)) {
            provider = providerOrConfig;
        }
        else if (providerOrConfig) {
            config = providerOrConfig;
        }
        this.logger = (config && config.logger) || new SimpleLogger((config && config.logLevel) || LogLevel.Error);
        // Use provided provider, provider URL or default lightnode URL.
        const rpcProvider = ((provider && typeof provider !== "string") ?
            provider :
            new MultiProvider(provider ?
                [provider] :
                [this.network.lightnode], this.logger));
        this.renVM = new RenVMProvider(rpcProvider, this.logger);
    }
}
// Expose constants so they can be accessed on the RenJS class
// e.g. `RenJS.Tokens`
RenJS.Tokens = Tokens;
RenJS.Networks = RenNetwork;
RenJS.NetworkDetails = NetworkDetails;
RenJS.Chains = Chain;
RenJS.utils = utils;
////////////////////////////////////////////////////////////////////////////////
// EXPORTS                                                                    //
// Based on https://github.com/MikeMcl/bignumber.js/blob/master/bignumber.js  //
////////////////////////////////////////////////////////////////////////////////
// tslint:disable: no-any no-object-mutation strict-type-predicates no-typeof-undefined
// tslint:disable-next-line: no-string-literal
RenJS["default"] = RenJS.RenJS = RenJS;
// AMD
try {
    // @ts-ignore
    if (typeof define === "function" && define.amd) {
        define(() => RenJS);
    }
}
catch (error) { /* ignore */ }
// Node.js and other environments that support module.exports.
try { // @ts-ignore
    if (typeof module !== "undefined" && module.exports) {
        module.exports = RenJS;
    }
}
catch (error) { /* ignore */ }
// Browser.
try {
    // @ts-ignore
    if (typeof window !== "undefined" && window) {
        window.RenJS = RenJS;
    }
}
catch (error) { /* ignore */ }
//# sourceMappingURL=data:application/json;base64,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